import React from "react";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import { Maximize2, ZoomIn, ZoomOut } from "lucide-react";

import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "./bpm.css";

/**
 * Read-only BPMN diagram with a cost/delay heatmap and finding badges.
 *
 * Uses NavigatedViewer (pan + zoom, no editing). Heat buckets, highlights and
 * selection are applied as canvas markers (CSS classes on the SVG element) and the
 * finding counts as overlays, so nothing in the BPMN itself is modified.
 */

export const HEAT_COLORS = ["#14375a", "#1e5650", "#6a6224", "#8f4d1c", "#9a2e26"];
const SEV_COLOR = { CRITICAL: "#ff6a33", HIGH: "#ffa685", MEDIUM: "#facb7e", LOW: "#92c8ff" };
const SEV_RANK = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
const MARKERS = ["bpm-heat-0", "bpm-heat-1", "bpm-heat-2", "bpm-heat-3", "bpm-heat-4",
                 "bpm-highlight", "bpm-dim", "bpm-selected"];

export const heatValue = (step, mode) =>
  (mode === "delay" ? step.annualDelayHours : step.annualCost) || 0;

const iconBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 28, height: 28, borderRadius: 7, cursor: "pointer",
  border: "1px solid #2c567b", background: "#0b2035", color: "#c3d2e0",
};

export default function BpmDiagram({
  xml, steps = [], findings = [], heatMode = "cost",
  highlight = [], selectedId = null, onSelect, height = 480,
}) {
  const hostRef = React.useRef(null);
  const viewerRef = React.useRef(null);
  const onSelectRef = React.useRef(onSelect);
  const [ready, setReady] = React.useState(0);
  const [error, setError] = React.useState("");

  React.useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  // One viewer per mount; StrictMode's double-invoke is handled by destroy().
  React.useEffect(() => {
    const viewer = new NavigatedViewer({
      container: hostRef.current,
      bpmnRenderer: {
        defaultFillColor: "#0f2a44",
        defaultStrokeColor: "#8aa0b6",
        defaultLabelColor: "#e8eef5",
      },
    });
    viewerRef.current = viewer;
    viewer.get("eventBus").on("element.click", (e) => {
      const el = e.element.labelTarget || e.element;
      const t = el.type || "";
      if (/Process|Participant|Lane|SequenceFlow|Collaboration/.test(t)) {
        onSelectRef.current?.(null);
      } else {
        onSelectRef.current?.(el.id);
      }
    });
    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !xml) return undefined;
    let dead = false;
    setError("");
    viewer.importXML(xml)
      .then(() => {
        if (dead) return;
        viewer.get("canvas").zoom("fit-viewport", "auto");
        setReady((r) => r + 1);
      })
      .catch((e) => !dead && setError(e.message || String(e)));
    return () => { dead = true; };
  }, [xml]);

  // Markers + overlays: recomputed from scratch whenever any input changes.
  React.useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !ready) return;
    const canvas = viewer.get("canvas");
    const overlays = viewer.get("overlays");
    const registry = viewer.get("elementRegistry");

    overlays.remove({ type: "bpm-badge" });
    registry.getAll().forEach((el) => MARKERS.forEach((m) => canvas.removeMarker(el, m)));

    const sevOf = Object.fromEntries(findings.map((f) => [f.id, f.severity]));
    const max = Math.max(0, ...steps.filter((s) => s.isTask).map((s) => heatValue(s, heatMode)));
    const hl = new Set(highlight);

    steps.forEach((s) => {
      if (!registry.get(s.id)) return;
      if (s.isTask) {
        const v = heatValue(s, heatMode);
        const bucket = max ? Math.min(4, Math.floor(Math.sqrt(v / max) * 5)) : 0;
        canvas.addMarker(s.id, `bpm-heat-${bucket}`);
      }
      if (s.findingIds?.length) {
        const worst = s.findingIds.map((id) => sevOf[id]).filter(Boolean)
          .sort((a, b) => SEV_RANK[b] - SEV_RANK[a])[0] || "LOW";
        overlays.add(s.id, "bpm-badge", {
          position: { top: -9, right: 9 },
          html: `<div class="bpm-badge" style="background:${SEV_COLOR[worst]}"
                      title="${s.findingIds.length} finding(s)">${s.findingIds.length}</div>`,
        });
      }
      if (hl.size) canvas.addMarker(s.id, hl.has(s.id) ? "bpm-highlight" : "bpm-dim");
    });
    if (selectedId && registry.get(selectedId)) canvas.addMarker(selectedId, "bpm-selected");
    if (hl.size) {
      const first = registry.get(highlight[0]);
      if (first) {
        try { canvas.scrollToElement(first, { top: 80, bottom: 80, left: 120, right: 120 }); }
        catch { /* older diagram-js without scrollToElement */ }
      }
    }
  }, [ready, steps, findings, heatMode, highlight, selectedId]);

  const zoom = (f) => {
    const canvas = viewerRef.current?.get("canvas");
    if (!canvas) return;
    if (f === "fit") canvas.zoom("fit-viewport", "auto");
    else canvas.zoom(canvas.zoom() * f);
  };

  return (
    <div style={{ position: "relative", height, borderRadius: 10, overflow: "hidden",
                  background: "#081a2c", border: "1px solid #1c3d5c" }}>
      <div ref={hostRef} className="bpm-canvas" style={{ position: "absolute", inset: 0 }} />
      <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 5 }}>
        <button style={iconBtn} title="Zoom in" onClick={() => zoom(1.2)}><ZoomIn size={14} /></button>
        <button style={iconBtn} title="Zoom out" onClick={() => zoom(1 / 1.2)}><ZoomOut size={14} /></button>
        <button style={iconBtn} title="Fit diagram" onClick={() => zoom("fit")}><Maximize2 size={14} /></button>
      </div>
      {error && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
                      justifyContent: "center", color: "#ffa685", fontSize: 12.5, padding: 20 }}>
          The BPMN diagram could not be rendered: {error}
        </div>
      )}
    </div>
  );
}
